import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { ContentTypes } from '../../constants/content-types.constants';
import { ProtoListRequest, ProtoResponse } from '@lsq-frontend/angular-shared/api-service';
import { FileType, Request, Response } from '@lsq-frontend/remote-services/enterprise/common';
import { WindowReferenceService } from '../window-reference/window-reference.service';
import { ApiService, ProtoApiService } from '@lsq-frontend/angular-shared/api-service';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';
import { SingletonServiceGuard } from '@lsq-frontend/angular-shared/features/generic-get-services';

@Injectable({ providedIn: 'root' })
export class DownloadService {
    public dataSubject = new Subject<Blob>();
    public dataStream: Observable<Blob>;

    constructor(
        private _alertService: AlertService,
        private _http: HttpClient,
        private _windowReferenceService: WindowReferenceService
    ) {
        SingletonServiceGuard.register('DownloadService');
        this.dataStream = this.dataSubject.asObservable();
    }

    public execute<TRequest extends Request, TResponse extends Response>(
        apiService:
            | ApiService<TRequest, TResponse>
            | ProtoApiService<ProtoListRequest, ProtoResponse>,
        request: TRequest,
        fileType: FileType,
        name: string
    ): Observable<Blob> {
        const contentType = this.getContentType(fileType);
        let headers = new HttpHeaders();
        headers = headers.set('Accept', `${contentType}; charset=utf-8`);

        const fileTypes = FileType;
        const extension = fileTypes[fileType].toLowerCase();

        this._http
            .post(apiService.endpointUrl, request, {
                headers: headers,
                reportProgress: true,
                responseType: 'blob'
            })
            .subscribe((blob) => {
                const filename = `${name}.${extension}`;
                this._windowReferenceService.downloadFile(blob, filename);
                this.dataSubject.next(blob);
            });

        return this.dataStream;
    }

    public executeForJava<TRequest extends Request, TResponse extends Response>(
        apiService:
            | ApiService<TRequest, TResponse>
            | ProtoApiService<ProtoListRequest, ProtoResponse>,
        request: TRequest,
        fileType: FileType,
        name: string
    ): Observable<Blob> {
        const contentType = ContentTypes.Json;
        let headers = new HttpHeaders();
        headers = headers.set('Accept', `${contentType}; charset=utf-8`);

        const fileTypes = FileType;
        const extension = fileTypes[fileType].toLowerCase();

        this._http
            .post(apiService.endpointUrl, request, {
                headers: headers,
                reportProgress: true,
                responseType: 'json'
            })
            .subscribe((obj) => {
                const decodedString = atob((obj as any).fileString);
                const fileBlob = new Blob([decodedString], {
                    type: this.getContentType(fileType)
                });
                const filename = `${name}.${extension}`;
                this._windowReferenceService.downloadFile(fileBlob, filename);
                this.dataSubject.next(fileBlob);
            });

        return this.dataStream;
    }

    public async executeUrl(
        downloadUrl: string,
        fileType: FileType,
        filename: string
    ): Promise<void> {
        const contentType = this.getContentType(fileType);
        let headers = new HttpHeaders();
        headers = headers.set('Accept', `${contentType}`);

        try {
            const blob = await this._http
                .get(downloadUrl, {
                    headers: headers,
                    reportProgress: true,
                    responseType: 'blob'
                })
                .toPromise();
            this._windowReferenceService.downloadFile(
                blob,
                `${filename}.${this.getFileTypeName(fileType)}`
            );
        } catch (error) {
            this._alertService.error('Sorry, the file could not be downloaded.');
            throw error;
        }
    }

    private getContentType(fileType: FileType): string {
        switch (fileType) {
            case FileType.Csv:
                return ContentTypes.Csv;
            case FileType.Pdf:
                return ContentTypes.Pdf;
            case FileType.Zip:
                return ContentTypes.Zip;
            case FileType.Tif:
                return ContentTypes.Tiff;
            case FileType.Jpg:
                return ContentTypes.Jpeg;
            case FileType.Png:
                return ContentTypes.Png;
            default:
                return ContentTypes.Json;
        }
    }

    private getFileTypeName(fileType: FileType): string {
        switch (fileType) {
            case FileType.Csv:
                return 'csv';
            case FileType.Pdf:
                return 'pdf';
            case FileType.Zip:
                return 'zip';
            case FileType.Tif:
                return 'tif';
            case FileType.Jpg:
                return 'jpeg';
            case FileType.Png:
                return 'png';
            default:
                return 'json';
        }
    }
}
